import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import theme from 'theme';
import i18n from 'i18n-js';
import { useConfirm } from 'hooks';
import PropTypes from 'prop-types';
import { Table } from 'components/UI';
import { FETCH_STATE } from 'constants';
import { Tooltip } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { redirectOAuthBrokerConnection } from 'utils/utils';
import { Box, Typography, IconButton } from '@material-ui/core';
import { OrganizationsActions } from 'state/modules/organizations';
import { BrokerConnectModal } from './BrokerConnectModal/BrokerConnectModal';
import { BrokerConnectSuccessModal } from './BrokerConnectModal/BrokerConnectSuccessModal';
import { BrokerConnectFailureModal } from './BrokerConnectModal/BrokerConnectFailureModal';

export const OrganizationBrokersWidget = ({ brokers, loading, refreshBrokers }) => {
  const dispatch = useDispatch();
  const {
    fetchedEditOrganizationToBrokers,
    fetchedOrganizationToBroker,
    currentOrganizationToBroker,
  } = useSelector((state) => state.organizations);
  const [currentBroker, setCurrentBroker] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openFailureModal, setOpenFailureModal] = useState(false);

  const [toogleDeleteBroker, DeleteBrokerConfirm] = useConfirm({
    title: i18n.t('organization.disconnect_broker'),
    message: i18n.t('organization.disconnect_broker_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: useCallback(() => {
      dispatch(
        OrganizationsActions.fetchDeleteOrganizationsToBrokers(currentBroker.org_to_broker_id)
      );
    }, [dispatch, currentBroker]),
  });

  const handleDeleteBroker = (broker) => {
    setCurrentBroker(broker);
    toogleDeleteBroker();
  };

  const handleConnect = (broker) => {
    dispatch(OrganizationsActions.fetchOrganizationToBrokerById(broker.org_to_broker_id));
  };

  const handleConnectBroker = (broker) => {
    setCurrentBroker(broker);
    if (broker?.auth_type === 'oauth') {
      handleConnect(broker);
    } else {
      dispatch(OrganizationsActions.resetBrokerSchema());
      setOpenModal(true);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setOpenSuccessModal(false);
  };

  const capitalizeFirstLetter = (str) => {
    return str[0].toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    if (fetchedOrganizationToBroker === FETCH_STATE.DONE && !!currentOrganizationToBroker) {
      redirectOAuthBrokerConnection(
        currentOrganizationToBroker.vendor_slug,
        currentOrganizationToBroker.auth_settings,
        currentOrganizationToBroker.name
      );
    }
  }, [fetchedOrganizationToBroker, currentOrganizationToBroker]);

  useEffect(() => {
    if (fetchedEditOrganizationToBrokers === FETCH_STATE.DONE) {
      setOpenModal(false);
      if (currentBroker?.auth_type === 'creds_manager') {
        setOpenSuccessModal(true);
      }
      refreshBrokers();
    } else if (fetchedEditOrganizationToBrokers === FETCH_STATE.ERROR) {
      setOpenModal(false);
      setOpenFailureModal(true);
      refreshBrokers();
    }
  }, [fetchedEditOrganizationToBrokers, currentBroker, refreshBrokers]);

  const columns = [
    {
      name: '',
      cell: (row) => (
        <Typography
          style={{
            color: theme.palette.secondary.dark,
            fontWeight: 700,
            fontSize: '16px',
          }}
        >
          {i18n.t(`brokers_slugs.${row.name}`, {
            defaultValue: capitalizeFirstLetter(row.name),
          })}
        </Typography>
      ),
      grow: 1,
    },
    {
      name: i18n.t('common.status'),
      center: true,
      selector: (row) => (
        <Typography
          style={{
            color: row.connected ? '#61C365' : 'inherit',
            fontWeight: 700,
            fontSize: '12px',
          }}
        >
          {row.connected ? i18n.t('brokers.connected') : i18n.t('brokers.disconnected')}
        </Typography>
      ),
      grow: 1,
    },
    {
      name: i18n.t('common.actions'),
      selector: 'action',
      center: true,
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            <Tooltip title={i18n.t('brokers.connect')}>
              <Box as="span">
                <IconButton
                  aria-label="connect"
                  disabled={row.connected}
                  size="medium"
                  onClick={() => {
                    handleConnectBroker(row);
                  }}
                >
                  <LinkIcon fontSize="inherit" style={{ rotate: '-45deg' }} />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={i18n.t('brokers.disconnect')}>
              <Box as="span">
                <IconButton
                  size="medium"
                  aria-label="disconnect"
                  disabled={!row.auth_type || !row.connected}
                  onClick={() => handleDeleteBroker(row)}
                >
                  <LinkOffIcon fontSize="inherit" style={{ rotate: '-45deg' }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <DeleteBrokerConfirm />
      <BrokerConnectModal open={openModal} broker={currentBroker} onClose={handleModalClose} />

      <BrokerConnectSuccessModal
        open={openSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
          dispatch(OrganizationsActions.resetBrokersState());
        }}
      />

      <BrokerConnectFailureModal
        open={openFailureModal}
        onClose={() => {
          setOpenFailureModal(false);
          dispatch(OrganizationsActions.resetBrokersState());
        }}
      />

      <Table data={brokers} columns={columns} pagination={false} loading={loading} />
    </>
  );
};

OrganizationBrokersWidget.propTypes = {
  brokers: PropTypes.array,
  loading: PropTypes.bool,
  refreshBrokers: PropTypes.func,
};
